import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { Company } from "@/types";
import { approveCompanyRegistration, rejectCompanyRegistration } from "@/services/RegisterCompany";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { getApiErrorCode } from "@/utils/apiError";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import RejectRegistrationModal from "./RejectRegistrationModal";

interface RegisteredCompanyActionsProps {
  company?: Company;
  onClose: () => void;
}

const DECISION_ERROR_KEYS: Record<string, string> = {
  "USR-251": "notifications:admin.registrationAlreadyDecided",
  "USR-254": "notifications:admin.reviverAdminRequired",
};

/** Approve/reject for a company created by self-registration (open COMPANY_REGISTERED). */
export default function RegisteredCompanyActions({ company, onClose }: RegisteredCompanyActionsProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const companyId = company?.id;

  const refreshAdminQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ["getCompaniesForAdmin"] });
    await queryClient.invalidateQueries({ queryKey: ["getCompanyById", companyId] });
  };

  const showDecisionError = (error: unknown, fallbackKey: string) => {
    const code = getApiErrorCode(error);
    const messageKey = (code && DECISION_ERROR_KEYS[code]) || fallbackKey;
    notifications.show(ErrorNotificationObj(t("notifications:generic.updateError"), t(messageKey)));
  };

  const { mutate: approve, isPending: isApproving } = useMutation({
    mutationFn: () => approveCompanyRegistration(companyId as number),
    onSuccess: async () => {
      await refreshAdminQueries();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.updateSuccess"), t("notifications:admin.companyApproved"))
      );
      onClose();
    },
    onError: (error) => showDecisionError(error, "notifications:admin.companyApprovedError"),
  });

  const { mutate: reject, isPending: isRejecting } = useMutation({
    mutationFn: (comment: string) => rejectCompanyRegistration(companyId as number, comment),
    onSuccess: async () => {
      await refreshAdminQueries();
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.updateSuccess"), t("notifications:admin.companyRejected"))
      );
      setRejectModalOpen(false);
      onClose();
    },
    // The modal stays open so the admin keeps the reason they typed.
    onError: (error) => showDecisionError(error, "notifications:admin.companyRejectedError"),
  });

  const isPending = isApproving || isRejecting;

  return (
    <>
      <RejectRegistrationModal
        opened={rejectModalOpen}
        loading={isRejecting}
        onClose={() => setRejectModalOpen(false)}
        onReject={(comment) => reject(comment)}
      />
      <Group align="flex-start" justify="flex-start" w="100%">
        <CTA
          intent="secondary"
          data-testid="registration-reject-btn"
          disabled={!companyId}
          loading={isPending}
          onClick={() => setRejectModalOpen(true)}
        >
          {t("actions:reject")}
        </CTA>
        <CTA data-testid="registration-approve-btn" disabled={!companyId} loading={isPending} onClick={() => approve()}>
          {t("actions:approve")}
        </CTA>
      </Group>
    </>
  );
}
